import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronRight, Loader2 } from "lucide-react";
import { shojinApi } from "../../lib/shojinApi";
import { trackEvent } from "../../lib/analytics";

/**
 * The 90-second Show-up Protocol.
 * Opens when a user is stalling on the Daily Call. 5 auto-advancing steps
 * scripted to bridge the moment between "I don't want to" and "I've started".
 */
export default function ShowUpProtocolModal({ open, onClose, onCommit }) {
  const [proto, setProto] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [secLeft, setSecLeft] = useState(0);

  useEffect(() => {
    if (!open) return;
    trackEvent("shojin_protocol_open");
    setStepIdx(0);
    setRunning(false);
    shojinApi.protocol().then((p) => {
      setProto(p);
      setSecLeft(p?.steps?.[0]?.sec || 15);
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !running || !proto) return;
    const t = setInterval(() => {
      setSecLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          setStepIdx((i) => {
            const next = i + 1;
            if (next >= proto.steps.length) {
              setRunning(false);
              return i;
            }
            setSecLeft(proto.steps[next].sec);
            return next;
          });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [running, open, proto, stepIdx]);

  const commit = useCallback(() => {
    trackEvent("shojin_protocol_commit");
    onCommit?.();
    onClose?.();
  }, [onCommit, onClose]);

  if (!open || !proto) return null;
  const step = proto.steps[stepIdx];
  const isLast = stepIdx === proto.steps.length - 1;

  return (
    <div
      role="dialog"
      aria-label="Show-up protocol"
      data-testid="shojin-protocol-modal"
      className="fixed inset-0 z-[10030] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3"
    >
      <div className="w-full max-w-md rounded-2xl border border-[#BEFF38]/30 bg-[#0b0b0c] shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">Shōjin Dō · 90 seconds</p>
            <p className="mt-0.5 text-[13px] text-white font-bold truncate">{step.title}</p>
          </div>
          <button onClick={onClose} data-testid="shojin-protocol-close" aria-label="Close" className="p-2 rounded-full text-zinc-400 hover:text-white hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-4 pt-3 flex gap-1.5">
          {proto.steps.map((_, i) => (
            <div key={i} className={`flex-1 h-1 rounded-full ${i < stepIdx ? "bg-[#BEFF38]" : i === stepIdx ? "bg-[#BEFF38]/60" : "bg-white/10"}`} />
          ))}
        </div>

        <div className="p-5 min-h-[220px] flex flex-col">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">{proto.purpose}</p>
          <p className="mt-3 text-white text-base leading-relaxed">{step.body}</p>
          {running && (
            <p className="mt-4 text-xs text-[#BEFF38] tabular-nums">Next in {secLeft}s</p>
          )}
        </div>

        <div className="p-4 border-t border-white/5 flex gap-2">
          {!running && !isLast && (
            <button
              onClick={() => { setRunning(true); setSecLeft(step.sec); }}
              data-testid="shojin-protocol-start"
              className="flex-1 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-xs py-3 rounded-lg hover:brightness-95"
            >
              Start 90 seconds
            </button>
          )}
          {running && !isLast && (
            <button
              onClick={() => {
                const next = stepIdx + 1;
                setStepIdx(next);
                setSecLeft(proto.steps[next].sec);
              }}
              data-testid="shojin-protocol-next"
              className="flex-1 border border-white/10 text-white font-black uppercase tracking-wider text-xs py-3 rounded-lg hover:bg-white/5 inline-flex items-center justify-center gap-1"
            >
              Skip step <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
          {isLast && (
            <button
              onClick={commit}
              data-testid="shojin-protocol-commit"
              className="flex-1 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-xs py-3 rounded-lg hover:brightness-95"
            >
              I'm going to train
            </button>
          )}
          {!isLast && (
            <button
              onClick={commit}
              data-testid="shojin-protocol-skip-all"
              className="text-zinc-500 hover:text-white font-black uppercase tracking-wider text-[10px] px-3"
            >
              I'm already ready
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
